/**
 * Which items a person sees in their current space.
 *
 * One rule for every surface that lists or counts items — the desktop item
 * list, the phone item list and the Pipeline Health report — so the count a
 * report shows is the count of the list it opens, on a laptop and on a phone.
 * Before this module the rule lived inside ItemListView alone: the desktop list
 * hid other spaces' items while the phone list and the report counted them.
 *
 * Canon 01 "Space": a scoping drawer inside a workspace, one of personal /
 * team / org. The visibility rules (unchanged from ItemListView):
 *
 *   Mandate space (settings.originModule = 'mandate'):
 *     only items with originModule = 'mandate'. Full isolation.
 *
 *   Every other space (personal, team, org):
 *     • spaceId == null            → shared / workspace-level; visible
 *     • spaceId == the current one → visible
 *     • spaceId == another space   → hidden, until "show all spaces"
 *     mandate items are never visible
 *
 * No current space, or spaces not loaded yet → nothing is hidden, so a page
 * does not flash empty while bootstrap is in flight.
 *
 * React-free; the store-reading hook is hooks/prioritization/useItemSpaceScope.ts.
 */

/** The slice of a space this rule reads (MySpace satisfies it). */
export interface SpaceLike {
  id: string;
  name: string;
  spaceType?: string | null;
  settings?: unknown;
}

export interface ItemSpaceScope {
  spaceId: string;
  spaceName: string;
  spaceType: string | null;
  /** A mandate space shows only mandate-origin items. */
  isMandate: boolean;
}

export interface SpaceScopeOptions {
  /** The desktop list's "Show org items" / "Show all spaces" toggle. */
  showAllSpaces?: boolean;
}

/** The slice of an item this rule reads. */
export interface SpaceScopedItem {
  spaceId?: string | null;
  originModule?: string | null;
}

export function resolveItemSpaceScope(
  currentSpaceId: string | null | undefined,
  mySpaces: ReadonlyArray<SpaceLike>,
): ItemSpaceScope | null {
  if (!currentSpaceId) return null;
  const space = mySpaces.find((s) => s.id === currentSpaceId);
  if (!space) return null;
  const settings = (space.settings ?? undefined) as Record<string, unknown> | undefined;
  return {
    spaceId: space.id,
    spaceName: space.name,
    spaceType: space.spaceType ?? null,
    isMandate: settings?.['originModule'] === 'mandate',
  };
}

export function isItemInSpaceScope(
  item: SpaceScopedItem,
  scope: ItemSpaceScope | null,
  options: SpaceScopeOptions = {},
): boolean {
  if (!scope) return true;
  if (scope.isMandate) return item.originModule === 'mandate';
  if (item.originModule === 'mandate') return false;
  if (options.showAllSpaces) return true;
  return item.spaceId == null || item.spaceId === scope.spaceId;
}

/**
 * The items visible in the scope. With no scope the input array itself comes
 * back, so a memo downstream keeps its identity.
 */
export function scopeItemsToSpace<T extends SpaceScopedItem>(
  items: T[],
  scope: ItemSpaceScope | null,
  options: SpaceScopeOptions = {},
): T[] {
  if (!scope) return items;
  return items.filter((item) => isItemInSpaceScope(item, scope, options));
}

/**
 * The words every surface uses for the scope, so the list and the report say
 * the same thing. null for no scope and for a mandate space (which has no
 * "show all" — its isolation is the point).
 */
export function describeSpaceScope(
  scope: ItemSpaceScope | null,
  options: SpaceScopeOptions = {},
): { label: string; toggleLabel: string } | null {
  if (!scope || scope.isMandate) return null;
  if (options.showAllSpaces) {
    return { label: 'Showing items from all spaces', toggleLabel: `Back to "${scope.spaceName}"` };
  }
  if (scope.spaceType === 'personal') {
    return { label: `Viewing: "${scope.spaceName}" + shared items`, toggleLabel: 'Show org items' };
  }
  return { label: `Viewing: "${scope.spaceName}"`, toggleLabel: 'Show all spaces' };
}
